import {
  ApiError,
  Layout,
  PrintService as BFFPrintService,
  RfidPrintIn,
} from '@/api/bff';
import { PrintService, TypeOfStore } from '@/api/receive';
import { Reprice, RepriceService, RepriceUpc } from '@/api/reprice';
import type { RootState } from '@/app/rootReducer';
import { AppDispatch } from '@/app/store';
// import { Printer } from '@/types/store';
import {
  REPRICE_DAYS_TO_SUBTRACT,
  REPRICE_DAYS_TO_SUM,
} from '@/configs/settings';
import { SapProduct } from '@/context/SapProduct/types';
import type { RepriceInitialState } from '@/types/reprice';
import { handlePrintBody } from '@/utils/handlePrintLayout';
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

const initialState: RepriceInitialState = {
  data: {
    dateRepriceList: [],
  },
  dates: [],
  currentDate: '',
  dataIsLoading: undefined,
  dataHasError: undefined,
  printIsLoading: undefined,
  printHasError: undefined,
};

type PromisesResultType = {
  rejected: number[];
  fullfilled: number[];
  listKeyDone: string[];
  listKeyFailure: string[];
  date: number;
};

const getProductPriceKeys = (
  index: number,
  list: string[],
  products: RepriceUpc[]
): string[] => {
  const { firstPrice, secondPrice } = products[index];

  const printHasDoneStatus = products[index].statusPrint === 'done';

  if (printHasDoneStatus) {
    return [''];
  } else {
    return [
      ...new Set([
        ...list,
        firstPrice?.keyFirstPrice ? firstPrice.keyFirstPrice : '',
        secondPrice?.keySecondPrice ? secondPrice.keySecondPrice : '',
      ]),
    ];
  }
};

export const getRepriceDates = createAsyncThunk<
  string[],
  void,
  {
    state: RootState;
    rejectValue: ApiError;
  }
>('reprice/getRepriceDates', async (_, { getState }) => {
  const { store } = getState().currentStore;

  if (store?.storeCode) {
    const response = await RepriceService.getRepriceDates({
      storeCode: store?.storeCode,
      daysToSubtract: REPRICE_DAYS_TO_SUBTRACT,
      daysToSum: REPRICE_DAYS_TO_SUM,
      orderBy: 'ASC',
    });

    if (response) {
      return response;
    }
  }

  return {
    dateRepriceList: [],
  };
});

export const getRepriceBrands = createAsyncThunk<
  Reprice,
  Omit<
    Parameters<typeof RepriceService.getRepriceProducts>[number],
    'storeCode'
  >,
  {
    state: RootState;
    rejectValue: ApiError;
  }
>('reprice/getRepriceBrands', async (request, { getState }) => {
  const { store } = getState().currentStore;

  if (store?.storeCode) {
    const response = await RepriceService.getRepriceProducts({
      storeCode: store?.storeCode,
      date: request.date,
    });

    if (response) {
      return response;
    }
  }

  return {
    dateRepriceList: [],
  };
});

export const getProductsPrintStatus = createAsyncThunk<
  Reprice,
  {
    requestBody: Omit<
      Parameters<
        typeof RepriceService.repricePrintStatus
      >[number]['requestBody'],
      'storeCode'
    >;
  },
  {
    state: RootState;
    rejectValue: ApiError;
  }
>(
  'reprice/getProductsPrintStatus',
  async (
    { requestBody: { date, listKeyDone, listKeyFailure } },
    { getState, rejectWithValue }
  ) => {
    try {
      const { store } = getState().currentStore;

      return await RepriceService.repricePrintStatus({
        requestBody: {
          storeCode: store?.storeCode || '',
          date,
          listKeyDone,
          listKeyFailure,
        },
      });
    } catch (err) {
      return rejectWithValue(err as ApiError);
    }
  }
);

export const printRepriceProducts = createAsyncThunk<
  void,
  {
    date: string;
    products: RepriceUpc[];
    upcPrintQuantity: Map<string, number>;
    selectedPrinter?: string;
    sapData: Record<string, SapProduct>;
  },
  {
    dispatch: AppDispatch;
    state: RootState;
    rejectValue: ApiError;
  }
>(
  'reprice/printRepriceProducts',
  async (
    { date, products, upcPrintQuantity, selectedPrinter, sapData },
    { getState, rejectWithValue, dispatch }
  ) => {
    const { store } = getState().currentStore;
    const printers = getState().currentStore.store?.printers || [];

    const repricePrint = async (
      product: RepriceUpc
      // storeCode: string
      // printers: Printer[]
    ): Promise<BFFPrintService> => {
      const {
        // brandDescription,
        // styleName,
        upcCode,
        firstPrice,
        secondPrice,
        statusPrint,
        numberItems,
      } = product;

      let layout = Layout.SRO_EMEA_RE_PRICING;

      if (product.firstPrice && product.firstPrice.currency === 'USD') {
        layout = Layout.SRO_NA_RE_PRICING;
      } else if (
        product.secondPrice &&
        product.secondPrice.currency === 'USD'
      ) {
        layout = Layout.SRO_NA_RE_PRICING;
      }

      const { typeOfStore } = store!;

      const productToPrint =
        await PrintService.receiveshipFindProductItemPrintDetails({
          upcCode: upcCode,
          storeCode: store?.storeCode || '',
          date: date,
        });

      const additionalBody: RfidPrintIn = {
        layout,
        upcCode: upcCode || '',
        serialNumberPrinter: selectedPrinter || printers[0].printerCode,
        copy_quantity:
          statusPrint !== 'not printed'
            ? upcPrintQuantity.get(upcCode || '') || 1
            : numberItems,
      };

      const printRequestBody = handlePrintBody(
        typeOfStore || TypeOfStore.AIRPORT_US,
        {
          ...productToPrint,
          retailPrice: firstPrice?.price,
          outletPrice:
            secondPrice?.type === 'OUTL' ? secondPrice.price : undefined,
          airportPrice:
            secondPrice?.type === 'AIRP' ? secondPrice.price : undefined,
          // description: secondPrice?.description,
          // percentage: secondPrice?.percentage,
          currency: firstPrice
            ? firstPrice.currency
            : secondPrice
            ? secondPrice.currency
            : undefined,
        },
        additionalBody
      );

      return await BFFPrintService.print({
        requestBody: {
          storeCode: store?.storeCode || '',
          RXable:
            sapData && product && product.upcCode && sapData[product.upcCode]
              ? sapData[product.upcCode]?.rxvalue || undefined
              : undefined,
          prescription:
            sapData && product && product.upcCode && sapData[product.upcCode]
              ? sapData[product.upcCode]?.prescription || undefined
              : undefined,
          ...printRequestBody,
        },
      });

      // return BFFPrintService.print({
      // 	requestBody: {
      // 		van: productToPrint.van,
      // 		marketingThemeDescription: productToPrint.marketingThemeDescription,
      // 		marketingStoryDescription: productToPrint.marketingStoryDescription,
      // 		frameColor: productToPrint.frameColor,
      // 		lensColor: productToPrint.lensColor,
      // 		polarCode: productToPrint.polarCode,
      // 		brandDescription: brandDescription,
      // 		styleNameDescription: styleName,
      // 		storeCode: storeCode,
      // 		layout: layout as Print['layout'],
      // 		upcCode,
      // 		price: firstPrice ? firstPrice.price : undefined,
      // 		currency: firstPrice
      // 			? firstPrice.currency
      // 			: secondPrice
      // 				? secondPrice.currency
      // 				: undefined,
      // 		second_price: secondPrice ? secondPrice.price : undefined,
      // 		serialNumberPrinter:
      // 			selectedPrinter || printers?.[0].printerCode || '',
      // 		copy_quantity:
      // 			statusPrint !== 'not printed'
      // 				? upcPrintQuantity.get(upcCode) || 1
      // 				: numberItems,
      // 	},
      // });
    };

    try {
      await Promise.allSettled(
        products.map((product): Promise<BFFPrintService> | undefined => {
          if (store?.storeCode) {
            return repricePrint(product);
            // if (store.printers) {
            // 	return repricePrint(product, store.storeCode, store.printers);
            // }
          }
          return undefined;
        })
      ).then(results => {
        const promises = results.reduce<PromisesResultType>(
          (prev, curr, index) => {
            if (curr.status === 'fulfilled') {
              const listKeyDone = [...prev.fullfilled, index]
                .flatMap(i =>
                  getProductPriceKeys(i, prev.listKeyDone, products)
                )
                .filter(key => key !== '');

              return {
                ...prev,
                fullfilled: [...prev.fullfilled, index],
                listKeyDone: listKeyDone,
              };
            } else {
              const listKeyFailure = [...prev.rejected, index]
                .flatMap(i =>
                  getProductPriceKeys(i, prev.listKeyFailure, products)
                )
                .filter(key => key !== '');
              dispatch(setPrintError(curr.reason as ApiError));
              return {
                ...prev,
                rejected: [...prev.rejected, index],
                listKeyFailure,
              };
            }
          },
          {
            rejected: [],
            fullfilled: [],
            listKeyDone: [],
            listKeyFailure: [],
            date: parseInt(date),
          }
        );

        if (
          promises.listKeyDone.length !== 0 ||
          promises.listKeyFailure.length !== 0
        ) {
          (async (): Promise<PayloadAction<Reprice | undefined | ApiError>> =>
            await dispatch(
              getProductsPrintStatus({
                requestBody: {
                  date: promises.date,
                  listKeyDone: promises.listKeyDone,
                  listKeyFailure: promises.listKeyFailure,
                },
              })
            ))();
        }
      });
    } catch (err) {
      return rejectWithValue(err as ApiError);
    }
  }
);

export const repriceSlice = createSlice({
  name: 'reprice',
  initialState,
  reducers: {
    initReprice: () => initialState,
    setPrintError: (state, { payload }: PayloadAction<ApiError>) => {
      state.printHasError = payload;
    },
    setCurrentDateSlice: (state, { payload }: PayloadAction<string>) => {
      state.currentDate = payload;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(getRepriceBrands.pending, state => {
        state.dataIsLoading = true;
      })
      .addCase(getRepriceDates.fulfilled, (state, { payload }) => {
        if (payload) {
          state.dates = payload;
        }
        state.dataIsLoading = false;
      })
      .addCase(getRepriceBrands.fulfilled, (state, { payload }) => {
        if (payload && payload.dateRepriceList) {
          state.data.dateRepriceList = payload.dateRepriceList;
        }
        state.dataIsLoading = false;
      })
      .addCase(getRepriceBrands.rejected, (state, { payload }) => {
        state.dataIsLoading = false;
        state.dataHasError = payload as ApiError;
      })
      .addCase(printRepriceProducts.pending, state => {
        state.printIsLoading = true;
      })
      .addCase(printRepriceProducts.fulfilled, state => {
        state.printIsLoading = false;
      })
      .addCase(printRepriceProducts.rejected, (state, { payload }) => {
        state.printIsLoading = false;
        state.printHasError = payload as ApiError;
      })
      .addCase(getProductsPrintStatus.pending, state => {
        state.dataIsLoading = true;
      })
      .addCase(getProductsPrintStatus.fulfilled, (state, { payload }) => {
        if (payload.dateRepriceList) {
          state.data.dateRepriceList = payload.dateRepriceList;
        }
        state.dataIsLoading = false;
      })
      .addCase(getProductsPrintStatus.rejected, (state, { payload }) => {
        state.dataIsLoading = false;
        state.dataHasError = payload as ApiError;
      });
  },
});

export const { initReprice, setPrintError, setCurrentDateSlice } =
  repriceSlice.actions;
export default repriceSlice.reducer;
